// src/pages/Comparisons.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CssBaseline,
  Stack,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  CompareArrows as CompareArrowsIcon,
  VisibilityOff,
  Visibility,
  // ContentCopy as CopyIcon,
  // ExpandMore as ExpandMoreIcon,
  // ExpandLess as ExpandLessIcon,
  // Check as CheckIcon,
  // Visibility as VisibilityIcon,
  // VisibilityOff as VisibilityOffIcon,
  // Edit as EditIcon
} from '@mui/icons-material';
import axios from "axios";
import config from "../../env.json";
import DatabaseMenu from "./DatabaseMenu";
import Legend from "../../components/LegendFloater/Legend";

interface Comparison {
  Id: number;
  Clone_Id: number;
  CloneTransaction_Sts: string; // Started / Completed / Failed
  EmployeeEmail_Adr: string;
}

interface Permission {
  Id: number;
  Permissions_Nm: string;
  OldPermissionDefaut_Val: string | null;
  NewPermissionDefaut_Val: string | null;
  Action_Typ: string; // INSERT / UPDATE
}

const Comparisons: React.FC = () => {
  const { cloneId } = useParams<{ cloneId: string }>();
  const [comparisons, setComparisons] = useState<Comparison[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedTxnId, setSelectedTxnId] = useState<number | null>(null);
  // const [permissions, setPermissions] = useState<Permission[]>([]);
  const [missingData, setMissingData] = useState<Permission[]>([]);
  const [differenceData, setDifferenceData] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [comparing, setComparing] = useState<boolean>(false);
  const [isViewMode, setIsViewMode] = useState(false);

  // state for confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [visibleValues, setVisibleValues] = useState<Record<string, boolean>>({});


  const navigate = useNavigate();

  const toggleVisibility = (key: string) => {
    setVisibleValues((prev) => ({ ...prev, [key]: !prev[key] }));
  };


  const fetchComparisons = async (refresh: boolean = false) => {
    try {
      if (!refresh)
        setLoading(true);
      const response = await axios.get(
        `${config.REACT_APP_ENV_API_BASE}/dbMigration/comparisons/${cloneId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("login")}`,
          },
        }
      );
      setComparisons(response.data?.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch comparisons.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cloneId) {
      fetchComparisons();
    }
  }, [cloneId]);

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <Chip label="Completed" color="success" size="small" />;
      case "started":
        return <Chip label="Started" color="info" size="small" />;
      case "failed":
        return <Chip label="Failed" color="error" size="small" />;
      case "discarded":
        return <Chip label="Discarded" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const handleApprove = async (txnId: number, transactionStatus: string = "") => {
    setSelectedTxnId(txnId);
    setIsViewMode(transactionStatus.toLowerCase() === "completed");

    setOpenModal(true);
    setLoadingPermissions(true);

    try {
      const response = await axios.get(
        `${config.REACT_APP_ENV_API_BASE}/dbMigration/transaction/${txnId}/selectedforApproval`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("login")}`,
          },
        }
      );
      // setPermissions(response.data?.data || []);
      const { missingData = [], differenceData = [] } = response.data?.data || {};
      setMissingData(missingData);
      setDifferenceData(differenceData);
    } catch (err) {
      console.error("Failed to fetch permissions", err);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleMigrate = async () => {
    if (!selectedTxnId) return;

    try {
      const response = await axios.post(
        `${config.REACT_APP_ENV_API_BASE}/dbMigration/execute`,
        { transactionId: selectedTxnId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("login")}`,
          },
        }
      );

      console.log("Migration response:", response.data);
      setOpenModal(false);
      await fetchComparisons(true);
    }
    catch (err) {
      console.error("Migration failed:", err);
      alert("Migration failed ❌");
    }
  };

  const handleStartComparison = async () => {
    try {
      // check if there's any transaction not completed/failed
      const hasActive = comparisons.some(
        (cmp) =>
          cmp.CloneTransaction_Sts.toLowerCase() !== "completed" &&
          cmp.CloneTransaction_Sts.toLowerCase() !== "failed"
      );

      if (hasActive) {
        setConfirmOpen(true);        // open confirmation dialog
        return;
      }

      await startComparison();
    } catch (err) {
      console.error("Failed to start comparison", err);
    }
  };

  // helper function to actually start comparison
  const startComparison = async () => {
    try {
      setComparing(true);
      const response = await axios.post(
        config.REACT_APP_ENV_API_BASE + `/dbMigration/compare`,
        {
          cloneId: cloneId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('login')}`,
          },
        }
      );
      console.log("Start comparison response:", response.data);
      // refresh comparisons list
      setComparisons((prev) => [response.data?.data, ...prev]);
      await fetchComparisons(true);
    } catch (error) {
      console.error("Failed to start comparison", error);
    }
    finally {
      setComparing(false);
      // setPendingComparison(false); // reset pending state
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box bgcolor="#f5f5f5" sx={{ overflowY: "auto", pr: 2 }}>
      {/* <DatabaseMenu /> */}

      <Card
        sx={{
          mt: "60px",
          mb: 4,
          borderLeft: "4px solid",
          borderColor: "primary.main",
          position: "relative",
          left: "80px",
          width: "92%",
        }}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Comparisons for Clone ID: {cloneId}
            </Typography>
            <Box>
              <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                Go Back
              </Button>
              <Button
                variant="contained"
                startIcon={
                  comparing ? <CircularProgress size={20} color="inherit" /> : <CompareArrowsIcon />
                } color="primary" onClick={handleStartComparison}
              >
                {comparing ? 'Comparing...' : 'Start Comparison'}
              </Button>
            </Box>
          </Stack>

          {comparisons.length === 0 ? (
            <Typography color="text.secondary">No comparisons found.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Initiated By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisons.map((cmp) => (
                    <TableRow key={cmp.Id} hover>
                      <TableCell>#{cmp.Id}</TableCell>
                      <TableCell>{getStatusChip(cmp.CloneTransaction_Sts)}</TableCell>
                      <TableCell>{cmp.EmployeeEmail_Adr}</TableCell>
                      <TableCell>
                        <Button
                          disabled={cmp.CloneTransaction_Sts.toLowerCase() !== "compared" && cmp.CloneTransaction_Sts.toLowerCase() !== "approval pending"}
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/DatabaseConfigurations/transaction/${cmp.Id}`)}
                          sx={{ mr: 1 }}
                        >
                          View Permissions
                        </Button>
                        {
                          cmp.CloneTransaction_Sts.toLowerCase() === "completed" ? (<Button
                            // disabled={cmp.CloneTransaction_Sts.toLowerCase() !== "approval pending"}
                            variant="contained"
                            size="small"
                            onClick={() => handleApprove(cmp.Id, cmp.CloneTransaction_Sts)}
                          >
                            View
                          </Button>) : (<Button
                            disabled={cmp.CloneTransaction_Sts.toLowerCase() !== "approval pending" && cmp.CloneTransaction_Sts.toLowerCase() !== "compared"}
                            variant="contained"
                            size="small"
                            onClick={() => handleApprove(cmp.Id)}
                          >
                            Approve
                          </Button>)
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Approve Modal */}
      {/* <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Approve Permissions for Transaction {selectedTxnId}</DialogTitle>
        <DialogContent dividers>
          {loadingPermissions ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : permissions.length === 0 ? (
            <Typography>No permissions found.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Permission</TableCell>
                  <TableCell>Old Value</TableCell>
                  <TableCell>New Value</TableCell>
                  <TableCell>Action Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((perm) => (
                  <TableRow key={perm.Id}>
                    <TableCell>{perm.Id}</TableCell>
                    <TableCell>{perm.Permissions_Nm}</TableCell>
                    <TableCell>{perm.OldPermissionDefaut_Val || "-"}</TableCell>
                    <TableCell>{perm.NewPermissionDefaut_Val || "-"}</TableCell>
                    <TableCell>{perm.Action_Typ}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleMigrate}>
            Migrate
          </Button>
        </DialogActions>
      </Dialog> */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="lg">
        {!isViewMode ?
          <DialogTitle>Approve Permissions for Transaction {selectedTxnId}</DialogTitle>
          :
          <DialogTitle>View Permissions for Transaction {selectedTxnId}</DialogTitle>
        }
        <DialogContent dividers>
          {loadingPermissions ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Missing Data */}
              <Typography variant="h6" gutterBottom>Missing Data</Typography>
              {missingData.length === 0 ? (
                <Typography color="text.secondary" gutterBottom>No missing data</Typography>
              ) : (
                <Table size="small" sx={{ mb: 3 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Permission</TableCell>
                      <TableCell>Old Value</TableCell>
                      <TableCell>New Value</TableCell>
                      <TableCell>Action Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingData.map((perm) => {
                      const oldKey = `missing-old-${perm.Id}`;
                      const newKey = `missing-new-${perm.Id}`;
                      return (
                      <TableRow key={perm.Id}>
                        <TableCell>{perm.Id}</TableCell>
                        <TableCell>{perm.Permissions_Nm}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography sx={{ mr: 1 }}>
                              {visibleValues[oldKey]
                                ? perm.OldPermissionDefaut_Val || "-"
                                : perm.OldPermissionDefaut_Val
                                  ? "•".repeat(8)
                                  : "-"}
                            </Typography>
                            {perm.OldPermissionDefaut_Val && (
                              <Tooltip title={visibleValues[oldKey] ? "Hide" : "Show"}>
                                <IconButton size="small" onClick={() => toggleVisibility(oldKey)}>
                                  {visibleValues[oldKey] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography sx={{ mr: 1 }}>
                              {visibleValues[newKey]
                                ? perm.NewPermissionDefaut_Val || "-"
                                : perm.NewPermissionDefaut_Val
                                  ? "•".repeat(8)
                                  : "-"}
                            </Typography>
                            {perm.NewPermissionDefaut_Val && (
                              <Tooltip title={visibleValues[newKey] ? "Hide" : "Show"}>
                                <IconButton size="small" onClick={() => toggleVisibility(newKey)}>
                                  {visibleValues[newKey] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        {/* <TableCell>{perm.OldPermissionDefaut_Val || "-"}</TableCell> */}
                        {/* <TableCell>{perm.NewPermissionDefaut_Val || "-"}</TableCell> */}
                        <TableCell>{perm.Action_Typ}</TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              )}

              {/* Difference Data */}
              <Typography variant="h6" gutterBottom>Difference Data</Typography>
              {differenceData.length === 0 ? (
                <Typography color="text.secondary">No difference data</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Permission</TableCell>
                      <TableCell>Old Value</TableCell>
                      <TableCell>New Value</TableCell>
                      <TableCell>Action Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {differenceData.map((perm) => (
                      <TableRow key={perm.Id}>
                        <TableCell>{perm.Id}</TableCell>
                        <TableCell>{perm.Permissions_Nm}</TableCell>
                        <TableCell>{perm.OldPermissionDefaut_Val || "-"}</TableCell>
                        <TableCell>{perm.NewPermissionDefaut_Val || "-"}</TableCell>
                        <TableCell>{perm.Action_Typ}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          {!isViewMode && (
            <Button variant="contained" color="primary" onClick={handleMigrate}>
              Migrate
            </Button>
          )}
        </DialogActions>
      </Dialog>


      {/* Confirmation Modal */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm New Comparison</DialogTitle>
        <DialogContent dividers>
          <Typography>
            There is already a transaction in progress. Do you still want to start a new comparison?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmOpen(false);
              startComparison();
            }}
          >
            Yes, Start New
          </Button>
        </DialogActions>
      </Dialog>
      <Legend />
    </Box>
  );
};

export default Comparisons;
